import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const prefixes = { bluebook: 'bluebook_session_', mock_variation: 'mockVariation_', listening: 'listening_session_' };
const timers = new Map();
const writes = new Map();
const clean = value => JSON.parse(JSON.stringify(value));
const owned = uid => !!uid && auth.currentUser?.uid === uid;

export const learningKey = (category, uid, identity) => `${prefixes[category]}${uid}_${identity}`;
export function readLocalSession(key, uid) {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return uid && value?.userId === uid ? value : null;
  } catch { return null; }
}
function writeRemote(key, session) {
  if (!owned(session.userId)) return Promise.resolve();
  const previous = writes.get(key) || Promise.resolve();
  const next = previous.catch(() => {}).then(() => {
    if (!owned(session.userId)) return;
    return setDoc(doc(db, 'learningProgress', key), clean(session));
  });
  writes.set(key, next);
  return next;
}
export function saveLearningSession(key, session, sync = true) {
  if (!owned(session.userId)) return;
  const previous = readLocalSession(key, session.userId);
  const value = { ...previous, ...session, storageKey: key, lastUpdateTime: Date.now() };
  localStorage.setItem(key, JSON.stringify(value));
  if (sync) {
    clearTimeout(timers.get(key));
    timers.set(key, setTimeout(() => {
    timers.delete(key);
    const latest = readLocalSession(key, session.userId);
    if (latest) writeRemote(key, latest).catch(() => {});
    }, 500));
  }
  return value;
}
export async function loadLearningSession(key, uid) {
  const local = readLocalSession(key, uid);
  if (!owned(uid)) return null;
  let timeout;
  try {
    const snapshot = await Promise.race([getDoc(doc(db, 'learningProgress', key)), new Promise((_, reject) => {
      timeout = setTimeout(() => reject(new Error('offline')), 6000);
    })]);
    const remote = snapshot.exists() ? snapshot.data() : null;
    if (!owned(uid)) return null;
    if (remote?.userId === uid && (!local || remote.lastUpdateTime > local.lastUpdateTime)) {
      localStorage.setItem(key, JSON.stringify(remote));
      return remote;
    }
  } catch { /* Continue from this device when offline. */ }
  finally { clearTimeout(timeout); }
  return local;
}

// Stable attempt ID means retrying a timed-out write never duplicates a result.
export async function submitLearningResult(key, uid, result) {
  if (!owned(uid)) throw new Error('학습 기록을 저장하려면 다시 로그인해주세요.');
  if (!result) throw new Error('저장할 결과가 없습니다.');
  const session = readLocalSession(key, uid) || { userId: uid };
  const resultId = session.resultId || `${key}_${session.startTime || Date.now()}`;
  const pending = { ...session, resultId, pendingResult: clean(result), isFinished: true, isCompleted: false };
  saveLearningSession(key, pending, false);
  writeRemote(key, { ...pending, lastUpdateTime: Date.now() }).catch(() => {});
  if (!owned(uid)) throw new Error('로그인한 계정이 변경되었습니다.');
  const request = setDoc(doc(db, 'examResults', resultId), {
    ...clean(result), userId: uid, attemptId: resultId,
    createdAt: serverTimestamp(), completedAt: result.completedAt || new Date().toISOString()
  });
  let timeout;
  try {
    await Promise.race([request, new Promise((_, reject) => {
      timeout = setTimeout(() => reject(new Error('연결 대기 중입니다. 답안은 이 기기에 보관했습니다.')), 12000);
    })]);
  } finally { clearTimeout(timeout); }
  const completed = { ...pending, pendingResult: null, isCompleted: true, lastUpdateTime: Date.now() };
  // Preserve a completed local copy; it must not appear as an unfinished attempt.
  saveLearningSession(key, completed, false);
  writeRemote(key, completed).catch(() => {});
  return resultId;
}

export async function retryPendingLearningResults(uid) {
  if (!owned(uid)) return;
  for (const key of Object.keys(localStorage)) {
    if (!Object.values(prefixes).some(prefix => key.startsWith(prefix))) continue;
    const session = readLocalSession(key, uid);
    if (session?.pendingResult) {
      await submitLearningResult(key, uid, session.pendingResult).catch(() => {});
    }
  }
}
